import { Check, ArrowRight } from "lucide-react";
import { PLANS, MEDIA } from "../mock";
import { useReveal } from "../hooks/useReveal";

const PlanCard = ({ plan, onSelect, idx }) => {
  const ref = useReveal();
  const isFeatured = plan.featured;
  return (
    <div
      ref={ref}
      data-testid={`plan-${plan.id}`}
      className={`reveal relative bg-[#121A33]/60 backdrop-blur-sm border ${
        isFeatured ? "border-[#C19A5D]" : "border-[#2A3451]"
      } p-10 lg:p-12 transition-all duration-500 hover:-translate-y-1`}
      style={{ transitionDelay: `${idx * 80}ms` }}
    >
      {isFeatured && (
        <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C19A5D] to-transparent" />
      )}

      {isFeatured && (
        <div className="relative -mt-12 -mx-12 mb-8 h-44 overflow-hidden bg-[#050814]">
          <img src={MEDIA.ecp} alt="ECP customized solution" className="w-full h-full object-cover opacity-80" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121A33] via-transparent to-transparent" />
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <span
          className={`overline ${
            isFeatured ? "text-[#C19A5D]" : "text-white/50"
          }`}
        >
          {plan.badge}
        </span>
        {isFeatured && (
          <span className="text-[10px] uppercase tracking-[0.2em] bg-[#C19A5D] text-[#050814] px-2 py-1 font-semibold">
            Most chosen
          </span>
        )}
      </div>

      <h3 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
        {plan.name}
      </h3>
      <p className="mt-3 text-white/65 leading-relaxed">{plan.tagline}</p>

      <div className="mt-8 pb-8 border-b border-white/10">
        <div className="flex items-baseline gap-2">
          <span className={`font-heading text-4xl sm:text-5xl font-semibold ${isFeatured ? "gold-text" : "text-white"}`}>
            {plan.price}
          </span>
          <span className="text-white/50 text-sm">{plan.cadence}</span>
        </div>
      </div>

      <ul className="mt-8 space-y-4">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-white/80">
            <Check className={`h-4 w-4 mt-0.5 shrink-0 ${isFeatured ? "text-[#C19A5D]" : "text-white/60"}`} strokeWidth={2} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        data-testid={`plan-cta-${plan.id}`}
        onClick={() => onSelect(plan.interest)}
        className={`group mt-10 w-full flex items-center justify-center gap-3 px-8 py-4 font-semibold uppercase tracking-wider text-xs transition-all duration-300 ${
          isFeatured
            ? "bg-[#C19A5D] text-[#050814] hover:bg-[#D4AF74]"
            : "bg-transparent text-white border border-white/20 hover:bg-white/5 hover:border-white/40"
        }`}
      >
        {plan.cta}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
      </button>
    </div>
  );
};

export const Pricing = ({ onSelect }) => {
  const ref = useReveal();
  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div ref={ref} className="reveal max-w-3xl mb-16">
          <span className="overline">Plans & solutions</span>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
            Standard, or <span className="gold-text">engineered for you.</span>
          </h2>
          <p className="mt-6 text-white/65 text-lg leading-relaxed">
            Choose the production-ready Template plan, or engage our Enterprise Choice Program (ECP) for a
            fully bespoke deployment shaped to your operation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {PLANS.map((p, i) => (
            <PlanCard key={p.id} plan={p} onSelect={onSelect} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
